import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react'

import { useLocation } from 'react-router-dom'

import {
  getHomePage,
  getPageById,
  getSelectedPage,
  getSpecialHomePage,
  updateCurrentEventById,
} from '@/actions/actions'
import { paths } from '@/constants/paths'
import useStore from '@/store/useStore'

export type PageRefresherContextProps = {
  pageRefresh: (search?: string) => void
}

export const PageRefresherContext = createContext<
  PageRefresherContextProps | undefined
>(undefined)

export const PageRefresherProvider = ({
  children,
}: PropsWithChildren<Record<string, unknown>>) => {
  const { pathname } = useLocation()
  const controller = useRef<Nullable<AbortController>>()
  const pageId = useStore((state) => state.page.page?.id)
  const eventId = useStore((state) => state.common.currentEvent?.id)

  useEffect(() => {
    return () => {
      controller.current?.abort()
    }
  }, [pathname])

  const pageRefresh = useCallback(
    (search?: string) => {
      controller.current?.abort()
      controller.current = new AbortController()
      const abortSignal = controller.current.signal
      if (
        pathname.includes(paths.CLASSIC_HOME) ||
        pathname.includes(paths.HOME)
      ) {
        void getHomePage(search)
      } else if (pathname.includes(paths.SPECIAL_HOME)) {
        void getSpecialHomePage(abortSignal, search)
      } else if (pathname.includes(paths.SELECTED_PAGE)) {
        void getSelectedPage(abortSignal, search)
      } else if (pathname.includes(paths.PAGE)) {
        void getPageById(`${pageId}${search}`, abortSignal)
      } else if (pathname.includes(paths.EVENT_PAGE)) {
        void updateCurrentEventById(`${eventId}${search}`, abortSignal)
      }
    },
    [pathname, pageId, eventId],
  )

  const context = {
    pageRefresh,
  }

  return (
    <PageRefresherContext.Provider value={context}>
      {children}
    </PageRefresherContext.Provider>
  )
}

export const usePageRefresher = (): PageRefresherContextProps => {
  const context = useContext(PageRefresherContext)

  if (!context) {
    throw new Error(
      'usePageRefresher must be used within an PageRefresherProvider',
    )
  }

  return context
}
